// @flow

import React from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';

import { WebsiteDefaultPage as BaseWebsiteDefaultPage } from 'components/Page';
import { PageType } from 'Constants';
import { OriginationFees } from 'data/global/footer/legal/numbered';
import { useImages } from 'hooks';
import { flex, Layout, maxWidthContainer, topBottomMargin, Width } from 'styles';
import { MarketsHero, ResourcesSideBar } from 'components/Content';

const METADATA = {
  title: 'Financial products to simplify finances, faster',
  description: `At Markets Direct, we’re transforming home equity by creating a fast, simple and transparent process to bring you closer to your financial goals. We'll show you how to use home equity to consolidate debt, pay for home improvement or fund your retirement. It's quick and easy. Let’s unlock the potential of your largest asset.`,
};

const PAGE_TYPE = {
  product: PageType.PRODUCT.NONE,
};

const WebsiteDefaultPage = styled(BaseWebsiteDefaultPage)`
  width: 100% !important;
`;

const Wrapper = styled.div`
  width: 100%;
  ${flex()};
  flex-direction: column;
  ${topBottomMargin([Layout.NAV_HEIGHT_MOBILE, 0], [Layout.NAV_HEIGHT_DESKTOP, 0], [Layout.NAV_HEIGHT_DESKTOP, 0])};
`;

const Content = styled.div`
  width: 100%;
  background: white;

  > div {
    padding: 40px 0;
    ${maxWidthContainer(1080)};
    ${flex()};
    flex-direction: column;

    @media (min-width: ${Width.BREAK_MD}px) {
      ${flex('flex-start', 'flex-start')};
      flex-direction: row;
    }
  }
`;

const Text = styled.div`
  white-space: pre-line;
`;

const PageContainer = ({ data }: { data: any }) => {
  const getImage = useImages(data);

  return (
    <WebsiteDefaultPage
      page={PAGE_TYPE}
      metaData={{
        ...METADATA,
        image: getImage('resources'),
      }}
      footerLegal={{
        numbered: OriginationFees,
      }}>
      <Wrapper>
        <MarketsHero
          title="RESOURCES"
          blurb="Hello? How can we help you?"
          image={{
            name: 'resources',
          }}
          images={data}
        />
        <Content>
          <div>
            <ResourcesSideBar sectionIndex={0} pageIndex={2}>
              <Text>
                <strong>1. Introduction</strong>
                {`
                
1.1 We are committed to safeguarding the privacy of our website visitors and users. In this policy we explain how we will handle your personal data.

1.2 By using our website and agreeing to this policy, you consent to our use of cookies in accordance with the terms of this policy.

1.3 This website is owned and operated by Triton Capital Markets Limited Ltd

1.4 You can contact us: K2, 1st Floor, Forni Complex, Valletta Waterfront, Floriana FRN 1913, T: +356 2013 3102; F: +356 2123 9190

1.5 Our data protection officer contact details are: Dr. M. Clara Galea Borg on DPO@markets-direct.com.mt

`}
                <strong>2. How we use your personal data</strong>
                {`

2.1 We may process data about your use of our website and services ("usage data").
The usage data may include your IP address, geographical location, browser type and version, operating system, referral source, length of visit, page views and website navigation paths, as well as information about the timing, frequency and pattern of your service use. The source of the usage data is our analytics tracking system. This usage data may be processed for the purposes of analysing the use of the website and services. The legal basis for this processing is our legitimate interests, namely monitoring and improving our website and services.

2.2 We may process your account data ("account data").
The account data may include your name,address, telephone number, email address, gender, date of birth, educational details, employment details and banking details. The source of the account data is you. The account data may be processed for the purposes of operating our website, providing our services, ensuring the security of our website and services, maintaining back-ups of our databases and communicating with you. The legal basis for this processing is the performance of a contract between you and us and/or taking steps, at your request, to enter into such a contract.

2.3 We may process your information included in your personal profile on our website ("profile data").
The profile data may include name, address, telephone number, email address, gender, date of birth, educational details, employment details and banking details. The profile data may be processed for the purposes of enabling and monitoring your use of our website and services. The legal basis for this processing is the performance of a contract between you and us and/or taking steps, at you request, to enter into such a contract.

2.4 We may process your personal data that are provided in the course of the use of our services("service data").
The service data may include name, address, telephone number, email address, gender, date of birth, educational details, employment details and banking details. The source of the service data is you. The service data may be processed for the purposes of operating our website, providing our services, ensuring the security of our website and services, maintaining back-ups of our databases and communicating with you. The legal basis for this processing is the performance of a contract between you and us and/or taking steps, at your request, to enter into such a contract..

2.5 We may process information contained in any enquiry you submit to us regarding products and/or services ("enquiry data").
The enquiry data may be processed for the purposes of offering, marketing and selling relevant products and/or services to you. The legal basis for this processing is consent provided to us when you signed the Customer Agreement with Triton Capital Markets Limited.

2.6 We may process information relating to transactions, including purchases of goods and services, that you enter into with us and/or through our website and platforms offered by us ("transaction data").
The transaction data may include your contact details, your bank details, your card details and the transaction details. The transaction data may be processed for the purpose of supplying the purchased goods and services and keeping proper records of those transactions. The legal basis for this processing is the performance of a contract between you and us and/or taking steps, at your request, to enter into such a contract.

2.7 We may process information that you provide to us for the purpose of subscribing to our email notifications and/or newsletters ("notification data").
The notification data may be processed for the purposes of sending you the relevant notifications and/or newsletters. The legal basis for this processing is the performance of a contract between you and us and/or taking steps, at your request, to enter into such a contract.

2.8 We may process information contained in or relating to any communication that you send to us ("correspondence data").
The correspondence data may include the communication content and metadata associated with the communication. Our website will generate the metadata associated with communications made using the website contact forms. The correspondence data may be processed for the purposes of communicating with you and record-keeping. The legal basis for this processing is the performance of a contract between you and us and/or taking steps, at your request, to enter into such a contract.

2.9 We may also process your personal data where such processing is necessary for compliance with a legal obligation to which we are subject.

`}
                <strong>3. Disclosure of Personal Data</strong>
                {`

Your personal data may be disclosed to third party service providers that process data on our behalf in accordance with specific terms (“Third Party Data Processors”). All third party data processors that process data on behalf of Triton Capital Markets Limited are obliged to enter into a data processing agreement to ensure that the same protection to your is applied. In addition to the specific disclosures of personal data set out in this Section 3, we may also disclose your personal data where such disclosure is necessary for compliance with a legal obligation.

`}
                <strong>4. International transfers of your personal data</strong>
                {`

The Third Party Data Processors that are used by Triton Capital Markets Limited may be located in the United States which provide equivalent protection in terms of the EU-US Privacy Shield decision which was adopted on the 12th July 2016 and the Privacy Shield framework which became operational on the 1st August 2016.

`}
                <strong>5. Retaining and deleting personal data</strong>
                {`

5.1 Personal data that we process for any purpose or purposes shall not be kept for longer than is necessary for that purpose or those purposes.

5.2 We will retain and delete your personal data as follows:
Your personal data shall be kept for a period of a minimum of 5 years from the date of closure of your account with us.

5.3 Notwithstanding the other provisions of this Section 5, we may retain your personal data where such retention is necessary for compliance with a legal obligation to which we are subject, or in order to protect your vital interests or the vital interests of another natural person.

`}
                <strong>6. Amendments</strong>
                {`

6.1 We may update this policy from time to time by publishing a new version on our website.

6.2 You should check this page occasionally to ensure you are happy with any changes to this policy.

`}
                <strong>7. Your rights</strong>
                {`

7.1 In terms of data protection legislation, you have the followings rights:
(a) the right to access;
This means that you have the right to ask us whether or not we process your data and if we do access to the personal data, together with certain additional information, i.e. the details of the purposes of the processing, the categories of personal data concerned and the recipients of the personal data. This will be provided to you free of charge.
(b) the right to rectification;
You have the right to have any inaccurate personal data about you rectified and, taking into account the purposes of the processing, to have any incomplete personal data about you completed.
(c) the right to erasure;
In some circumstances you have the right to the erasure of your personal data without undue delay.
(d) the right to restrict processing;
In some circumstances you have the right to restrict the processing of your personal data.
(e) the right to object to processing;
In some circumstances you have the right to object to processing of your personal data.
(f) the right to data portability;
In some circumstances you have the right to request a portable copy of your personal data.
(g) the right to complain to a supervisory authority;
You have the right to submit a complaint with a supervisory authority responsible for data protection.
(h) the right to withdraw consent.
To the extent that the legal basis for our processing of your personal information is consent, you have the right to withdraw that consent at any time. Withdrawal will not affect the lawfulness of processing before the withdrawal.

`}
                <strong>8. About cookies</strong>
                {`

8.1 A cookie is a file containing an identifier (a string of letters and numbers) that is sent by a webserver to a web browser and is stored by the browser.
The identifier is then sent back to the server each time the browser requests a page from the server.

8.2 Cookies may be either "persistent" cookies or "session" cookies: a persistent cookie will be stored by a web browser and will remain valid until its set expiry date, unless deleted by the user before the expiry date; a session cookie, on the other hand, will expire at the end of the user session, when the web browser is closed.

8.3 Cookies do not typically contain any information that personally identifies a user, but personal information that we store about you may be linked to the information stored in and obtained from cookies.

`}
                <strong>9. Cookies that we use</strong>
                {`

9.1 We use cookies for the following purposes:
To identify you so that further related information can be looked up on a server-side database. For example the first time that you visit our website and enter your details on our portal, you may choose a username which is stored in the cookie, and then provide data such as password, name, address, preferred font size, page layout, etc. - this information would all be stored on the database using the username as a key. When you visit the Triton Capital Markets Limited Website again and log into the Triton Capital Markets Limited portal again the server will read the cookie to find your username, and then retrieve your information from the database without it having to be re-entered.

`}
                <strong>10. Cookies used by our service providers</strong>
                {`

10.1 Our service providers use cookies and those cookies may be stored on your computer when you visit our website.

10.2 We use Google Analytics to analyse the use of our website.
Google Analytics gathers information about website use by means of cookies. The information gathered relating to our website is used to create reports about the use of our website. Google's privacy policy is available at: https://www.google.com/policies/privacy/.

`}
                <strong>11. Managing cookies</strong>
                {`

11.1 you can learn more about cookies here: http://www.whatarecookies.com/

Triton Capital Markets Limited Ltd. is registered with the Office of Information and Data Protection and Data Protection Commissioner of Malta.

We may share your personal information with any member of our group, which means any subsidiaries, ultimate holding companies and its respective subsidiaries may receive such information. We may also share your information with certain third parties including business partners, suppliers and sub-contractors and advertisers. If you wish to qualify, vary, modify or limit your consent in any way then please contact your customer service representative at Triton Capital Markets Limited.

We are committed to protecting our customers' private information. In the course of providing you with financial products and services, we need to collect and maintain certain private information about you.
Triton Capital Markets Limited does not sell your personal information to anyone.

We collect private information about you from the following sources:

Information we receive from you on applications or other forms.
Information about your transactions with Triton Capital Markets Limited, LTD "Triton Capital Markets Limited Global".
We do not disclose any private information about you or former customers to anyone other than affiliates, except as permitted or required by law. For example, this may include providing information to companies that perform support services on our behalf or to providing tax information to the relevant authorities.
We restrict access to your private information to those employees who need to know that information to provide products or services to you.
We maintain physical, electronic, and procedural safeguards to protect private information.
Should have any questions regarding your account, please contact the Compliance Department at telephone number (+356) 2013-3475.`}
              </Text>
            </ResourcesSideBar>
          </div>
        </Content>
      </Wrapper>
    </WebsiteDefaultPage>
  );
};

export default PageContainer;

export const query = graphql`
  query {
    imageToWebP: allFile(filter: { relativeDirectory: { eq: "pages/resources" }, extension: { nin: ["svg", "gif"] } }) {
      edges {
        node {
          ...ImageToWebP
        }
      }
    }
    otherImage: allFile(filter: { relativeDirectory: { eq: "pages/resources" }, extension: { in: ["svg", "gif"] } }) {
      edges {
        node {
          ...OtherImage
        }
      }
    }
  }
`;
